import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Check, Sparkles } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { authService } from '@/services/auth.service';
import { toast } from 'sonner';

export const UpgradeModal = ({ wedding, trigger, onUpgradeSuccess }) => {
  const [open, setOpen] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const { activeOrg } = useAuth();

  // Mock price - in production, this would come from backend
  const price = 999; // ₹999 per wedding credit

  const handleUpgrade = async () => {
    if (!activeOrg?.id) {
      toast.error('No organization selected');
      return;
    }

    try {
      setUpgrading(true);
      // Mock upgrade - add 1 credit (in production, this would integrate with payment gateway)
      await authService.upgradeOrganization(activeOrg.id, 1);
      
      // If this is for a specific wedding, mark it as upgraded
      if (wedding?.id) {
        // In production, backend would handle converting trial to paid
        // For now, we'll just show success
      }
      
      toast.success('Wedding upgraded successfully. All features are now unlocked.');
      setOpen(false);
      onUpgradeSuccess?.();
    } catch (error) {
      console.error('Upgrade error:', error);
      toast.error('Failed to upgrade. Please try again.');
    } finally {
      setUpgrading(false);
    }
  };

  return (
    <>
      {trigger && (
        <div onClick={() => setOpen(true)}>
          {trigger}
        </div>
      )}
      
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-full bg-primary/10">
                <Sparkles className="h-6 w-6 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-2xl">Upgrade This Wedding</DialogTitle>
                <DialogDescription className="text-base pt-1">
                  Unlock full access and continue planning without limits.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <h4 className="font-semibold mb-3">What you get:</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>Unlimited guests, vendors, tasks, events</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>Full menu & budget access</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>Unlimited guest emails</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>Client collaboration</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>No expiry</span>
                </li>
              </ul>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={upgrading}
              className="text-sm text-muted-foreground"
            >
              Decide later
            </Button>
            <Button
              onClick={handleUpgrade}
              className="gradient-primary flex-1 sm:flex-initial"
              disabled={upgrading}
            >
              {upgrading ? 'Processing...' : `Upgrade for ₹${price.toLocaleString()}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
